import React from 'react';

/**
 * Session-storage key used to track whether we've already attempted a
 * page reload for a stale-chunk error.  This prevents infinite reload loops.
 */
const RELOAD_FLAG = 'chunk_reload_attempted';

/**
 * Detects whether an error is caused by a stale / missing dynamic-import chunk.
 *
 * After a new deployment the content-hashed filenames change, but users whose
 * browser still holds the old HTML will request chunk URLs that no longer exist
 * on the server, producing one of the following error patterns:
 *
 * - "Failed to fetch dynamically imported module: …"
 * - "Loading chunk … failed"
 * - "Loading CSS chunk … failed"
 * - Network errors during import (TypeError from fetch)
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    // Vite-specific: import fails with a generic TypeError when the server
    // returns 404 for the missing chunk file.
    (error.name === 'TypeError' && msg.includes('failed to fetch'))
  );
}

/**
 * Wrapper around `React.lazy` that automatically retries a failed dynamic
 * import **once** by reloading the page so the browser fetches the fresh
 * `index.html` (which references the current chunk filenames).
 *
 * A `sessionStorage` flag prevents infinite reloads: if the page was already
 * reloaded for a chunk error and the import still fails, the error is allowed
 * to propagate so the Suspense fallback / error boundary can handle it.
 *
 * Usage:
 * ```ts
 * const MyComponent = lazyWithRetry(
 *   () => import('./MyComponent'),
 * );
 * // or with a named export:
 * const MyComponent = lazyWithRetry(
 *   () => import('./MyComponent').then(m => ({ default: m.MyComponent })),
 * );
 * ```
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    importFn().catch((error: unknown) => {
      if (!isChunkLoadError(error)) {
        // Not a chunk-load error — rethrow so normal error handling applies.
        throw error;
      }

      const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === 'true';

      if (!alreadyReloaded) {
        // First failure → mark & reload to fetch the new HTML + chunk manifest.
        sessionStorage.setItem(RELOAD_FLAG, 'true');
        window.location.reload();
        // Return a never-resolving promise so React doesn't try to render
        // anything before the reload happens.
        return new Promise<{ default: T }>(() => {});
      }

      // Second failure after reload → clear flag and let the error surface.
      sessionStorage.removeItem(RELOAD_FLAG);
      throw error;
    }),
  );
}

/**
 * Call once on app startup (e.g. in main.tsx) to clear the reload flag on a
 * successful page load. This way, subsequent navigations that hit *new*
 * stale-chunk errors will get a fresh reload attempt.
 */
export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_FLAG);
}
